import React, { useMemo, useState } from 'react';
import { Button, Alert } from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import DispatchDeliverySummaryFilter from './components/DispatchDeliverySummaryFilter';
import DispatchDeliverySummaryTable from './components/DispatchDeliverySummaryTable';
import { useDispatchDeliverySummary } from './hooks/useDispatchDeliverySummary';
import type { DispatchDeliverySummaryGroupingMode } from './graphql/queries/getDispatchDeliverySummary';
import { useWarehouseContext } from '../../../../contexts/WarehouseContext';
import { useCurrentDsp } from '../../../../hooks/useCurrentDsp';
import { SiteType } from '../../../../graphql/types/SiteType';

const DispatchDeliverySummaryPage: React.FC = () => {
    const { t } = useTranslation();
    const { currentWarehouse } = useWarehouseContext();
    const { dspId: currentDspId, dsp: currentDsp, isDspManager: isDspUser } = useCurrentDsp();
    const [dspExpanded, setDspExpanded] = useState(false);
    const [whsExpanded, setWhsExpanded] = useState(false);

    const groupingMode = useMemo<DispatchDeliverySummaryGroupingMode>(() => {
        // 两个都折叠：需要按照这两个分组：DSP_WAREHOUSE
        if (!dspExpanded && !whsExpanded) {
            return 'NONE';
        }
        // 只展开 DSP：DSP
        if (dspExpanded && !whsExpanded) {
            return 'DSP';
        }
        // 只展开 WHS：WAREHOUSE
        if (!dspExpanded && whsExpanded) {
            return 'WAREHOUSE';
        }
        // 两个都展开-无需分组：NONE
        if (dspExpanded && whsExpanded) {
            return 'DSP_WAREHOUSE';
        }
        // 默认返回 NONE（理论上不会到达这里）
        return 'NONE';
    }, [dspExpanded, whsExpanded]);

    const {
        filters,
        setFilters,
        activeDimension,
        setActiveDimension,
        pagination,
        handlePageChange,
        records,
        total,
        loading,
        error,
        data
    } = useDispatchDeliverySummary(groupingMode);

    const renderContent = () => {
        return (
            <DispatchDeliverySummaryTable
                data={records}
                loading={loading}
                page={pagination.page}
                pageSize={pagination.size}
                total={total}
                onPageChange={handlePageChange}
                dspExpanded={dspExpanded}
                whsExpanded={whsExpanded}
                onToggleDsp={() => setDspExpanded(prev => !prev)}
                onToggleWhs={() => setWhsExpanded(prev => !prev)}
                dimension={activeDimension}
            />
        );
    };

    // 获取计算时间显示文本
    const getCalculatedAtText = () => {
        const isDataCalculated = data?.getDispatchDeliverySummaryPage.isDataCalculated ?? true;
        
        // 只有在数据未计算时才显示"数据计算中"
        if (!isDataCalculated) {
            return t('wms.delivery.dispatchDeliverySummary.dataCalculating');
        }
        
        // 如果数据已计算，但有记录，显示计算时间
        if (records.length > 0 && records[0].calculatedAt) {
            // calculatedAt 可能是字符串格式的时间戳或 ISO 字符串
            const calculatedAtValue = typeof records[0].calculatedAt === 'string' 
                ? (isNaN(Number(records[0].calculatedAt)) 
                    ? records[0].calculatedAt 
                    : parseInt(records[0].calculatedAt))
                : records[0].calculatedAt;
            
            const calculatedAt = new Date(calculatedAtValue).toLocaleString('zh-CN', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: false
            }).replace(/\//g, '-');
            return `${t('wms.delivery.dispatchDeliverySummary.dataUpdatedAt')}：${calculatedAt}`;
        }
        
        // 数据已计算但没有记录，不显示任何提示
        return '';
    };

    return (
        <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">
            <DispatchDeliverySummaryFilter
                filters={filters}
                onFiltersChange={setFilters}
                dimension={activeDimension}
            />
            {error && (
                <Alert
                    message="查询错误"
                    description={error.message || JSON.stringify(error)}
                    type="error"
                    showIcon
                    className="mt-4"
                />
            )}
            <div className="mt-4 mb-4 flex items-center justify-between gap-4">
                <div className="flex gap-4">
                    <Button
                        type={activeDimension === 'DAY' ? 'primary' : 'default'}
                        onClick={() => setActiveDimension('DAY')}
                        className="w-auto min-w-24"
                    >
                        <span className="font-bold">{t('wms.delivery.dispatchDeliverySummary.buttons.byDay')}</span>
                    </Button>
                    <Button
                        type={activeDimension === 'WEEK' ? 'primary' : 'default'}
                        onClick={() => setActiveDimension('WEEK')}
                        className="w-auto min-w-24"
                    >
                        <span className="font-bold">{t('wms.delivery.dispatchDeliverySummary.buttons.byWeek')}</span>
                    </Button>
                    <Button
                        type={activeDimension === 'MONTH' ? 'primary' : 'default'}
                        onClick={() => setActiveDimension('MONTH')}
                        className="w-auto min-w-24"
                    >
                        <span className="font-bold">{t('wms.delivery.dispatchDeliverySummary.buttons.byMonth')}</span>
                    </Button>
                </div>
                <div className="flex items-center gap-4">
                    {getCalculatedAtText() && (
                        <div style={{ color: '#ff4d4f' }}>
                            <strong>{getCalculatedAtText()}</strong>
                        </div>
                    )}
                    <Button
                        type="default"
                        onClick={() => {
                            const defaultDateRange: [Dayjs, Dayjs] = [dayjs().subtract(7, 'day'), dayjs()];
                            const isWHS = currentWarehouse?.siteType === SiteType.WAREHOUSE;
                            const isDSP = isDspUser;
                            
                            // 根据权限设置默认值
                            if (isWHS && currentWarehouse?.name) {
                                setFilters({
                                    dateRange: defaultDateRange,
                                    warehouseOptions: [{
                                        label: currentWarehouse.name,
                                        value: Number(currentWarehouse.id)
                                    }],
                                    dspOptions: undefined
                                });
                            } else if (isDSP && currentDspId) {
                                setFilters({
                                    dateRange: defaultDateRange,
                                    warehouseOptions: undefined,
                                    dspOptions: [{
                                        value: Number(currentDspId),
                                        label: currentDsp?.name ?? `DSP #${currentDspId}`
                                    }]
                                });
                            } else {
                                setFilters({
                                    dateRange: defaultDateRange,
                                    warehouseOptions: undefined,
                                    dspOptions: undefined
                                });
                            }
                        }}
                        className="w-auto min-w-24"
                    >
                        <span className="font-bold">{t('components.button.reset')}</span>
                    </Button>
                </div>
            </div>
            {renderContent()}
        </div>
    );
};

export default DispatchDeliverySummaryPage;

